import { useEffect, useRef, useState } from 'react';
import { BRAND } from './wedgeBrand';
import { isNativeIOS } from './native/nativeBridge';

/**
 * WedgeUpdatePrompt · "New version available - tap to update" banner.
 *
 * WHY THIS EXISTS (and why it is NOT a service-worker updatefound flow):
 * The only service worker this app ships is /wedge-push-sw.js, which is
 * push-only (no fetch handler, no precache) and already calls skipWaiting +
 * clients.claim. It never changes per deploy and cannot serve a stale bundle,
 * so the classic registration.waiting / updatefound mechanism has nothing to
 * hook onto here. nginx already serves index.html with `no-store`, so a normal
 * browser tab picks up a new hashed bundle on its next navigation. The failure
 * mode reps actually hit is the iOS standalone PWA: it keeps the running
 * app-shell alive in memory across app switches and does not re-navigate until
 * the rep force-quits, so a fresh deploy stays invisible. That is a runtime
 * problem, not a caching-SW problem, so the fix is a runtime version check.
 *
 * MECHANISM:
 * - On mount we fingerprint the RUNNING build from the hashed asset URLs in the
 *   live DOM (Vite emits /assets/<name>-<hash>.js|css and injects them here).
 * - We periodically re-fetch /index.html with `cache: 'no-store'` and fingerprint
 *   the DEPLOYED build the same way. A difference means a new deploy is live.
 * - When they differ we show a tappable banner. The rep taps -> location.reload()
 *   so a rep is NEVER hard-reloaded underneath a mid-action. No auto reload.
 *
 * NO RELOAD LOOP (three guards):
 * 1. The reload only ever happens on an explicit tap. Nothing here reloads on
 *    its own, so an infinite loop is structurally impossible.
 * 2. First install / dev / any unfingerprintable state is a no-op: if the
 *    running signature is empty (dev serves /src/main.tsx, not hashed assets),
 *    polling never starts and the banner never shows.
 * 3. Per-session de-dupe: once the rep taps update for a given deployed
 *    signature we record it in sessionStorage. If that reload somehow does not
 *    land the new shell (a stubborn iOS cache), we do NOT re-prompt for that
 *    same signature this session, so tapping can never spiral. A genuine
 *    relaunch starts a fresh session and gives the update another chance.
 */

const INDEX_URL = '/index.html';
const POLL_MS = 60_000;
// Ignore foreground re-checks that fire closer together than this so rapid
// app-switching on iOS does not hammer the network.
const MIN_FOREGROUND_GAP_MS = 15_000;
// sessionStorage key holding the deployed signature we already asked to reload
// for (guard #3). Session-scoped on purpose so a real relaunch retries.
const ATTEMPTED_KEY = 'rv_wedge_update_attempted';

/** Extract a stable fingerprint of the build from an HTML string or from the
 *  live document markup: the sorted set of module entry scripts Vite injects
 *  STATICALLY into index.html (`<script type="module" src="/assets/index-<hash>.js">`).
 *  The entry hash changes on every build (its code embeds the hashed names of
 *  every chunk it imports, and CSS ships inside the JS), so it identifies a
 *  deploy on its own.
 *
 *  2026-09-23 · was "every /assets/*.js|css path in the markup". That included
 *  the `<link rel="modulepreload">` tags Vite's preload helper APPENDS to head
 *  at runtime for dynamic imports (main.tsx pulls ./wedgeA2hs before first
 *  render), so the running signature always carried one chunk the fetched
 *  index.html did not and every fresh web visitor got a false "New version
 *  available" banner ~8s after load (verified on wedge.repvantage.io:
 *  running = index + vendor-react + wedgeA2hs, deployed = index + vendor-react).
 *  Only module SCRIPT tags are compared now; Vite never appends those at
 *  runtime, so the two sides are symmetric. Pinned by wedgeUpdateSignature.test.ts. */
export function assetSignature(html: string): string {
  const found = new Set<string>();
  const re = /<script\b[^>]*\btype=["']module["'][^>]*\bsrc=["'](\/assets\/[A-Za-z0-9._-]+\.js)["']/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) found.add(m[1]);
  return Array.from(found).sort().join('|');
}

function runningSignature(): string {
  if (typeof document === 'undefined') return '';
  try {
    return assetSignature(document.documentElement.outerHTML);
  } catch {
    return '';
  }
}

export function WedgeUpdatePrompt() {
  /* 2026-07-27 · disabled on native (checked after hooks for hook-order
     safety). The shell ships its OWN bundled assets, but nativeShellFetch
     rewrites /index.html to wedge.repvantage.io — so this component
     fingerprinted the WEB deploy, could never match the bundled shell's
     signature, and showed a permanent false "New version available" banner
     on every cold launch whose tap reloaded the same bundle. Native updates
     come from the App Store, full stop. */
  const native = isNativeIOS();
  const [available, setAvailable] = useState(false);
  // Captured once. Empty in dev / when unfingerprintable -> feature disabled.
  const runningRef = useRef<string>(runningSignature());
  const lastCheckRef = useRef<number>(0);
  const busyRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || native) return undefined;
    const running = runningRef.current;
    // Guard #2: no hashed-asset fingerprint (dev server, or parse failure) ->
    // never poll, never prompt. First paint is untouched in every case.
    if (!running) return undefined;

    let cancelled = false;

    const check = async () => {
      if (cancelled || busyRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      busyRef.current = true;
      try {
        const res = await fetch(`${INDEX_URL}?rv_uc=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res.ok) return;
        const html = await res.text();
        const deployed = assetSignature(html);
        // Bail on an unparseable response rather than risk a false prompt.
        if (!deployed || deployed === running) return;
        // Guard #3: already asked to reload for this exact deployed build this
        // session -> stay silent so a stubborn cache cannot cause a tap-loop.
        let attempted = '';
        try {
          attempted = sessionStorage.getItem(ATTEMPTED_KEY) || '';
        } catch {
          /* storage blocked · treat as not-yet-attempted */
        }
        if (deployed === attempted) return;
        if (!cancelled) setAvailable(true);
      } catch {
        /* offline / transient · try again on the next tick */
      } finally {
        busyRef.current = false;
        lastCheckRef.current = Date.now();
      }
    };

    const id = window.setInterval(check, POLL_MS);
    const onForeground = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckRef.current < MIN_FOREGROUND_GAP_MS) return;
      void check();
    };
    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('focus', onForeground);
    // One deferred first check so it never competes with initial paint.
    const kick = window.setTimeout(check, 8_000);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.clearTimeout(kick);
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('focus', onForeground);
    };
  }, []);

  if (native || !available) return null;

  const onUpdate = () => {
    try {
      // Re-fingerprint at tap time so the guard records the newest deployed
      // build, not whatever was live when the banner first appeared.
      void (async () => {
        try {
          const res = await fetch(`${INDEX_URL}?rv_uc=${Date.now()}`, {
            cache: 'no-store',
            credentials: 'same-origin',
          });
          if (res.ok) {
            const sig = assetSignature(await res.text());
            if (sig) sessionStorage.setItem(ATTEMPTED_KEY, sig);
          }
        } catch {
          /* best-effort · reload proceeds regardless */
        } finally {
          window.location.reload();
        }
      })();
    } catch {
      window.location.reload();
    }
  };

  return (
    <button
      type="button"
      onClick={onUpdate}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        // Banner is pinned to top:0, so the notch clearance belongs on TOP
        // (safe-area-inset-top), not the bottom.
        padding: 'calc(10px + env(safe-area-inset-top)) 16px 10px',
        background: BRAND.accent,
        color: BRAND.accentInk,
        fontFamily: BRAND.fontBody,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: BRAND.accentInk,
        }}
      />
      New version available · tap to update
    </button>
  );
}
